import { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type { AdditionalInputField } from '@/types/checkout';
import { AdyenPayFormUI } from './AdyenPayFormUI';
import { type AdyenPaymentResponse } from '@/types/payment';
import { ProviderEnum, type PaymentMethodEnum } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { isEmailValid } from '@/utils/email';
import { AdyenInputType } from '@/types/adyen';

interface Props {
  selectedPaymentMethod: PaymentMethodEnum;
}

export const AdyenPayForm = ({ selectedPaymentMethod }: Props) => {
  const {
    paymentMethodsSettings,
    totalPrice,
    customer,
    actions: { runPayment, setIsFormValid },
  } = useContext(CheckoutContext);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { t } = useTranslate();
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const paymentMethod = useMemo(() => {
    return paymentMethodsSettings.find((method) => method.paymentMethodName === selectedPaymentMethod);
  }, [paymentMethodsSettings, selectedPaymentMethod]);

  const additionalInputFields = useMemo(() => {
    if (!paymentMethod) {
      return [];
    }
    const paymentMethodFields = paymentMethodsSettings.find(
      (method) => method.id === paymentMethod.id
    )?.additionalInputFields;
    return paymentMethodFields;
  }, [paymentMethodsSettings, paymentMethod]);

  const validateField = useCallback(
    (field: AdditionalInputField, value: string): string => {
      if (!value) {
        return t('fieldErrors.incomplete', { field: field.display_name });
      }

      if (field.validation) {
        try {
          const regex = new RegExp(field.validation);
          if (!regex.test(value)) {
            return t('fieldErrors.invalidFormat', { field: field.display_name.toLowerCase() });
          }
        } catch (error) {
          console.error(`Invalid regex pattern for ${field.apm_key}:`, error);
        }
      }

      return '';
    },
    [t]
  );

  const handlePayment = useCallback(async () => {
    await runPayment<AdyenPaymentResponse>(fieldValues, undefined, ProviderEnum.ADYEN);
  }, [fieldValues, runPayment]);

  const handleFieldChange = useCallback(
    (field: AdditionalInputField, value: string) => {
      setFieldValues((prev) => ({
        ...prev,
        [field.apm_key]: value,
      }));
    },
    [validateField]
  );

  const handleFieldBlur = useCallback(
    (field: AdditionalInputField, value: string) => {
      const error = validateField(field, value);

      if (error) {
        setFieldErrors((prev) => ({
          ...prev,
          [field.apm_key]: error,
        }));
      } else {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field.apm_key];
          return newErrors;
        });
      }
    },
    [validateField]
  );

  // Update global form validity
  const isFormValid = useMemo(() => {
    const fieldsWithoutEmail = additionalInputFields?.filter(
      (field) => field.apm_key !== AdyenInputType.EMAIL && field.apm_key !== AdyenInputType.SHOPPER_EMAIL
    );
    if (
      !additionalInputFields ||
      additionalInputFields.length === 0 ||
      (isEmailValid(customer?.email) && fieldsWithoutEmail?.length === 0 && isZipCodeValid)
    ) {
      return true;
    }

    const allFieldsFilled = additionalInputFields.every(
      (field) => fieldValues[field.apm_key] && fieldValues[field.apm_key].trim() !== ''
    );
    const noErrors = Object.keys(fieldErrors).length === 0;

    return allFieldsFilled && noErrors && isZipCodeValid && isEmailValid(customer?.email);
  }, [fieldValues, fieldErrors, additionalInputFields, customer?.email, isZipCodeValid]);

  useEffect(() => {
    setIsFormValid(isFormValid);
  }, [isFormValid, setIsFormValid]);

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  if (!additionalInputFields || additionalInputFields.length === 0) {
    return null;
  }

  return (
    <AdyenPayFormUI
      handleTaxValidationChange={handleTaxValidationChange}
      additionalInputFields={additionalInputFields}
      fieldValues={fieldValues}
      fieldErrors={fieldErrors}
      totalPrice={totalPrice}
      onFieldChange={handleFieldChange}
      onFieldBlur={handleFieldBlur}
      onPayment={handlePayment}
      isDisabled={!isFormValid || !isEmailValid(customer?.email)}
    />
  );
};
